import { Extension } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { history, redo, undo } from "@tiptap/pm/history";
import { Fragment, Slice, type Mark, type Node, type NodeType, type Schema } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

/**
 * One paragraph of plain text, hard breaks, and skill chips.
 * Avoid StarterKit: it statically imports every rich-text extension.
 */
const QueryBarDocument = Document.extend({
  content: "paragraph",
});

/**
 * Default clipboard parsing turns each newline / block into a paragraph.
 * Those extra blocks cannot fit this schema, so paste must become hardBreaks.
 */
const QueryBarClipboard = Extension.create({
  name: "queryBarClipboard",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("queryBarClipboard"),
        props: {
          clipboardTextParser(text, $context) {
            return sliceFromPastedPlainText($context.parent.type.schema, text, $context.marks());
          },
          transformPasted(slice, view) {
            return flattenPastedSlice(slice, view.state.schema);
          },
        },
      }),
    ];
  },
});

function sliceFromPastedPlainText(schema: Schema, text: string, marks: readonly Mark[]): Slice {
  return openParagraphSlice(schema, plainTextToInlineNodes(schema, text, marks));
}

function plainTextToInlineNodes(schema: Schema, text: string, marks: readonly Mark[]): Node[] {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const markSet = marks.length > 0 ? marks : undefined;
  const nodes: Node[] = [];
  lines.forEach((line, index) => {
    if (line) nodes.push(schema.text(line, markSet));
    if (index < lines.length - 1) {
      nodes.push(schema.nodes.hardBreak.create());
    }
  });
  return nodes;
}

function isHardBreak(node: Node, hardBreak: NodeType): boolean {
  return node.type === hardBreak;
}

function isBlockish(node: Node | undefined): boolean {
  return !!node && (node.isTextblock || node.isBlock);
}

/** True when a textblock is empty or only has placeholder `<br>` children. */
function isPlaceholderTextblock(node: Node, hardBreak: NodeType): boolean {
  for (let i = 0; i < node.childCount; i++) {
    if (!isHardBreak(node.child(i), hardBreak)) return false;
  }
  return true;
}

/**
 * Flatten pasted blocks into one paragraph of inlines.
 * Insert a hardBreak at each textblock boundary, but skip placeholder
 * `<p><br></p>` children and sibling `<br>` nodes between blocks — clipboard
 * HTML uses those as empty-line markup, and copying them would double the break.
 */
export function flattenPastedSlice(slice: Slice, schema: Schema): Slice {
  const inline: Node[] = [];
  const hardBreak = schema.nodes.hardBreak;

  const collect = (node: Node, prev?: Node, next?: Node) => {
    if (node.isTextblock) {
      if (inline.length > 0) inline.push(hardBreak.create());
      if (isPlaceholderTextblock(node, hardBreak)) return;
      node.content.forEach((child) => inline.push(child));
      return;
    }
    if (node.isBlock) {
      collectFragment(node.content);
      return;
    }
    if (isHardBreak(node, hardBreak) && (isBlockish(prev) || isBlockish(next))) return;
    inline.push(node);
  };

  const collectFragment = (fragment: Fragment) => {
    const nodes: Node[] = [];
    fragment.forEach((node) => nodes.push(node));
    nodes.forEach((node, index) => collect(node, nodes[index - 1], nodes[index + 1]));
  };

  collectFragment(slice.content);
  return openParagraphSlice(schema, inline);
}

function openParagraphSlice(schema: Schema, inline: Node[]): Slice {
  const paragraph = schema.nodes.paragraph.create(null, Fragment.fromArray(inline));
  return new Slice(Fragment.from(paragraph), 1, 1);
}

const QueryBarHistory = Extension.create({
  name: "queryBarHistory",
  addProseMirrorPlugins() {
    return [history()];
  },
  addKeyboardShortcuts() {
    return {
      "Mod-z": () => undo(this.editor.state, this.editor.view.dispatch),
      "Mod-y": () => redo(this.editor.state, this.editor.view.dispatch),
      "Shift-Mod-z": () => redo(this.editor.state, this.editor.view.dispatch),
    };
  },
});

const QueryBarPlaceholder = Extension.create<{ placeholder: string }>({
  name: "queryBarPlaceholder",
  addOptions() {
    return { placeholder: "" };
  },
  addProseMirrorPlugins() {
    const placeholder = this.options.placeholder;
    return [
      new Plugin({
        key: new PluginKey("queryBarPlaceholder"),
        props: {
          decorations: ({ doc }) => {
            if (!isComposerEmpty(doc)) return null;
            const paragraph = doc.firstChild;
            if (!paragraph) return null;
            return DecorationSet.create(doc, [
              Decoration.node(0, paragraph.nodeSize, {
                class: "is-editor-empty",
                "data-placeholder": placeholder,
              }),
            ]);
          },
        },
      }),
    ];
  },
});

function isComposerEmpty(doc: Node): boolean {
  if (doc.textContent.length > 0) return false;
  let hasMention = false;
  doc.descendants((node) => {
    if (node.type.name === "skillMention") {
      hasMention = true;
      return false;
    }
    return true;
  });
  return !hasMention;
}

export function createQueryBarPlainTextExtensions(placeholder: string) {
  return [
    QueryBarDocument,
    Paragraph,
    Text,
    HardBreak,
    QueryBarClipboard,
    QueryBarHistory,
    QueryBarPlaceholder.configure({ placeholder }),
  ];
}
